import { eyeClose, Move, Reset, See } from "assets/icons/Common";
import classNames from "classnames";
import { GoButton, Row } from "components/primitives";
import { CurrencyValues } from "constants/widgets";
import React, { forwardRef, useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { MoveIconBtn } from "../FreeText/FreeText.styles";

interface DroppedItem {
  key: number;
  id: number;
  image: string;
  x: number;
  y: number;
  containerIndex: number;
  type: string;
  value: number;
  width: number;
  height: number;
}

interface Currency {
  currency?: string;
  currencySign: string;
  data: Data[];
}

interface Data {
  key: number;
  image: string;
  value: number;
  type: string;
  width: string;
  height: string;
}

const Money: React.FC = ({ pixelPosition, settings }: any) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const priceTagRef = useRef<HTMLDivElement>(null);
  const theme = settings.config.theme;
  const width = pixelPosition.width / 6;
  const height = pixelPosition.height / 6;
  const coinWidth = pixelPosition.width / 12;
  const target = +settings.config.target;
  const targetLabel = settings.config.target;

  const [draggItems, setDragItems] = useState<Currency>(CurrencyValues[settings.currency]);
  const [droppedItems, setDroppedItems] = useState<DroppedItem[]>([]);
  const [randomCurrencyValue, setRadomCurrencyValue] = useState<number>(0);
  const [randomCurrencyValueLabel, setRadomCurrencyValueLabel] = useState<string>("0");
  const [isChangeCorrect, setIsChangeCorrect] = useState<boolean | null>(null);
  const [flexContainer, setFlexContainer] = useState(1);
  const [type, setType] = useState(settings.option);
  const [toggleTotal, setTogalTotal] = useState(true);

  const containerDimensionRef = useRef({
    x: pixelPosition.width,
    y: pixelPosition.height,
  });

  let itemId = useRef(1);

  useEffect(() => {
    setRadomCurrencyValue(+settings.config.range);
    setRadomCurrencyValueLabel(settings.config.range);
  }, [settings.config.range]);

  useEffect(() => {
    setFlexContainer(theme === "money_lab" ? +settings.format : 2);
    setDroppedItems([]);
  }, [settings.format, theme]);

  useEffect(() => {
    setType(settings.type);
  }, [settings.type]);

  useEffect(() => {
    reset();
    if (theme === "exchange") {
      generateRandomCurrency(target);
    }
    setDragItems(CurrencyValues[settings.currency]);
  }, [theme, target]);

  useEffect(() => {
    reset();
    setDragItems(CurrencyValues[settings.currency]);
  }, [settings.currency]);

  useEffect(() => {
    const mapItemPosition = droppedItems.map((item) => {
      const isIncreased =
        pixelPosition.width >= containerDimensionRef.current.x &&
        pixelPosition.height >= containerDimensionRef.current.y;

      const x = isIncreased ? item.x + 0.5 : item.x - 0.5;
      const y = isIncreased ? item.y + 0.5 : item.y - 0.5;
      return { ...item, x, y };
    });
    containerDimensionRef.current = {
      x: pixelPosition.width,
      y: pixelPosition.height,
    };
    setDroppedItems(mapItemPosition);
  }, [pixelPosition.width, pixelPosition.height]);

  useEffect(() => {
    const floats = draggItems.data
      .map((item: Data) => item.value)
      .filter((value) => value % 1 !== 0);

    const allowedDifferences: any = floats;
    const randomBase = settings.config.range;

    const closestDifference = allowedDifferences.reduce((prev: any, curr: any) =>
      Math.abs(randomBase % curr) < Math.abs(randomBase % prev) ? curr : prev,
    );

    const roundedValue = Math.round(randomBase / closestDifference) * closestDifference;
    const finalValue = +roundedValue.toFixed(2);
    setRadomCurrencyValue(finalValue);
  }, [settings.config.range, draggItems]);

  const generateRandomCurrency = (value?: number) => {
    let finalValue;

    if (value) {
      finalValue = value;
    } else {
      const floats = draggItems.data.filter((item) => item.value % 1 !== 0);

      const allowedDifferences: any = floats;
      const randomBase = Math.random() * 100;

      const closestDifference = allowedDifferences
        .map((item: Data) => item.value)
        ?.reduce((prev: any, curr: any) =>
          Math.abs(randomBase % curr) < Math.abs(randomBase % prev) ? curr : prev,
        );

      const roundedValue = Math.round(randomBase / closestDifference) * closestDifference;

      finalValue = Math.round(roundedValue * 20) / 20;
    }

    const formattedValue = parseFloat(finalValue.toFixed(2));

    const formattedWithTrailingZeroes = formattedValue.toFixed(2);

    setRadomCurrencyValue(parseFloat(formattedWithTrailingZeroes));
    setRadomCurrencyValueLabel(formattedWithTrailingZeroes);
    setIsChangeCorrect(null);
    setDroppedItems([]);
  };

  const handleDragStart = (e: React.MouseEvent, item: any, dropped = false) => {
    e.preventDefault();

    const cloneElement = document.createElement("div");
    Object.assign(cloneElement.style, cloneStyles);

    const cloneImage = document.createElement("img");
    cloneImage.src = item.image;
    cloneImage.alt = item.name || "Cloned image";

    Object.assign(cloneImage.style, {
      width: `${item.type === "coin" ? coinWidth : width}px`,
      height: `${height}px`,
      pointerEvents: "none",
      objectFit: "contain",
    });

    cloneElement.appendChild(cloneImage);
    document.body.appendChild(cloneElement);

    const mouseMoveHandler = (event: MouseEvent) =>
      handleMouseMove(event, cloneElement, item, dropped);
    const mouseUpHandler = (event: MouseEvent) =>
      handleMouseUp(event, cloneElement, item, mouseMoveHandler, mouseUpHandler, dropped);

    document.addEventListener("mousemove", mouseMoveHandler);
    document.addEventListener("mouseup", mouseUpHandler);

    updateClonePosition(cloneElement, e.pageX, e.pageY, item, dropped);
  };

  const handleMouseMove = (
    e: MouseEvent,
    cloneElement: HTMLDivElement,
    item: DroppedItem,
    dropped = false,
  ) => {
    if (cloneElement) {
      updateClonePosition(cloneElement, e.pageX, e.pageY, item, dropped);
    }
  };

  const clamp = (value: number, min: number, max: number) => {
    return Math.max(min, Math.min(max, value));
  };

  const handleMouseUp = (
    e: MouseEvent,
    cloneElement: HTMLDivElement,
    item: any,
    mouseMoveHandler: (event: MouseEvent) => void,
    mouseUpHandler: (event: MouseEvent) => void,
    dropped = false,
  ) => {
    if (dropZoneRef.current) {
      const gridParts = Array.from(dropZoneRef.current.children) as HTMLDivElement[];

      const dropZoneRect = dropZoneRef.current.getBoundingClientRect();
      const itemRect = cloneElement.getBoundingClientRect();

      gridParts.forEach((part, index) => {
        const partRect = part.getBoundingClientRect();
        part.style.borderColor = "#ccc";

        if (
          itemRect.left >= partRect.left &&
          itemRect.right <= partRect.right &&
          itemRect.top >= partRect.top &&
          itemRect.bottom <= partRect.bottom
        ) {
          // if (isMoneyLab) {
          //   const amount = droppedItems
          //     .filter((item1) => item1.containerIndex === index)
          //     .reduce((a, c) => a + c.value, item.value);

          //   if (amount.toFixed(2) > limit) {
          //     return;
          //   }
          // }

          const offsetX = e.clientX - dropZoneRect.left - itemRect.width / 2;
          const offsetY = e.clientY - dropZoneRect.top - itemRect.height / 2;

          const clampedX = clamp(offsetX, 0, dropZoneRect.width - itemRect.width);
          const clampedY = clamp(offsetY, 0, dropZoneRect.height - itemRect.height);
          setIsChangeCorrect(null);
          if (dropped) {
            const updatedDropped = droppedItems.filter((value) => item.id !== value.id);
            setDroppedItems([
              ...updatedDropped,
              {
                key: item.key,
                image: item.image,
                id: itemId.current++,
                x: clampedX,
                y: clampedY,
                containerIndex: index,
                type: item.type,
                value: item.value,
                width: item.width,
                height: item.height,
              },
            ]);
          } else {
            setDroppedItems((prevItems) => [
              ...prevItems,
              {
                key: item.key,
                image: item.image,
                id: itemId.current++,
                x: clampedX,
                y: clampedY,
                containerIndex: index,
                type: item.type,
                value: item.value,
                width: item.width,
                height: item.height,
              },
            ]);
          }
        }
      });
    }

    const draggableRef = containerRef.current;
    if (draggableRef && dropped) {
      const rect = draggableRef.getBoundingClientRect();
      if (
        e.clientX > rect.left &&
        e.clientX < rect.right &&
        e.clientY > rect.top &&
        e.clientY < rect.bottom
      ) {
        draggableRef.style.backgroundColor = "#ffffff";
        remove(item.id);
      }
    }

    if (cloneElement) {
      cloneElement.remove();
    }
    document.removeEventListener("mousemove", mouseMoveHandler);
    document.removeEventListener("mouseup", mouseUpHandler);
  };

  const updateClonePosition = (
    cloneElement: HTMLDivElement,
    x: number,
    y: number,
    item: DroppedItem,
    dropped = false,
  ) => {
    // Add border color to grids

    {
      /* if (dropZoneRef.current) {
      if (isMoneyLab) {
        const gridParts = Array.from(
          dropZoneRef.current.children
        ) as HTMLDivElement[];
        gridParts.forEach((part, index) => {
          const partRect = part.getBoundingClientRect();
          if (
            x > partRect.left &&
            x < partRect.right &&
            y > partRect.top &&
            y < partRect.bottom
          ) {
            const amount = droppedItems
              .filter((item1) => item1.containerIndex === index)
              .reduce((a, c) => a + c.value, item.value);
            if (amount.toFixed(2) > limit) {
              gridParts[index].style.borderColor = "red";
            }
          }
        });
      }
    } */
    }

    const draggableRef = containerRef.current;
    if (draggableRef && dropped) {
      const rect = draggableRef.getBoundingClientRect();

      if (x > rect.left && x < rect.right && y > rect.top && y < rect.bottom) {
        draggableRef.style.backgroundColor = "#ffe6e6";
      } else {
        draggableRef.style.backgroundColor = "#ffffff";
      }
    }

    cloneElement.style.left = `${x - cloneElement.offsetWidth / 2}px`;
    cloneElement.style.top = `${y - cloneElement.offsetHeight / 2}px`;
    cloneElement.style.zIndex = "9999";
  };

  const cloneStyles: React.CSSProperties = {
    position: "absolute",
    opacity: 0.5,
    pointerEvents: "none",
  };

  const checkChange = () => {
    if (priceTagRef?.current) {
      priceTagRef.current.classList.toggle("scale");
    }

    let moneyGiven: any = droppedItems
      .filter((item) => item.containerIndex === 0)
      .map((item) => item.value);
    moneyGiven = moneyGiven.reduce((a: number, c: number) => a + c, 0).toFixed(2);

    let change: any = droppedItems
      .filter((item) => item.containerIndex === 1)
      .map((item) => item.value);
    change = +change.reduce((a: number, c: number) => a + c, 0).toFixed(2);

    const calculatedChange = +(moneyGiven - change).toFixed(2);

    setTimeout(() => {
      if (calculatedChange === randomCurrencyValue) {
        setIsChangeCorrect(true);
      } else {
        setIsChangeCorrect(false);
      }
      if (priceTagRef?.current) {
        priceTagRef.current.classList.toggle("scale");
      }
    }, 1000);
  };

  const remove = (id: number) => {
    const tempItems = droppedItems.filter((item) => item.id !== id);
    setDroppedItems(tempItems);
  };

  const toggleView = () => {
    setTogalTotal(!toggleTotal);
  };

  const checkTotal = (index: number) => {
    const amount = droppedItems
      .filter((item1) => item1.containerIndex === index)
      .reduce((a, c) => a + c.value, 0);
    const refs = dropZoneRef.current?.children;

    if (refs?.length) {
      const gridRefs = Array.from(refs) as HTMLDivElement[];
      gridRefs.forEach((ref, refIndex) => {
        if (refIndex === index) {
          const childRef: any = Array.from(ref.children)[0];
          if (!amount && childRef) {
            childRef.style.backgroundColor = "#8f84de";
            childRef.style.color = "#ffffff";
            return;
          }
          if (childRef && childRef?.style) {
            childRef.classList.toggle("scale-chip");

            setTimeout(() => {
              if (target === +amount.toFixed(2)) {
                childRef.classList.toggle("scale-chip");
                childRef.style.backgroundColor = "green";
                childRef.style.color = "#ffffff";
              } else {
                childRef.classList.toggle("scale-chip");
                childRef.style.backgroundColor = "red";
                childRef.style.color = "#ffffff";
              }
            }, 1000);
          }
        }
      });
    }
  };

  const reset = () => {
    setDroppedItems([]);
    setIsChangeCorrect(null);
    setRadomCurrencyValue(0);
    setRadomCurrencyValue(0);
    setRadomCurrencyValueLabel("0");

    const refs = dropZoneRef.current?.children;
    if (refs?.length) {
      const gridRefs = Array.from(refs) as HTMLDivElement[];
      gridRefs.forEach((ref, refIndex) => {
        const childRef: any = Array.from(ref.children)[0];

        if (childRef) {
          childRef.style.backgroundColor = "#8f84de";
          childRef.style.color = "#ffffff";
        }
      });
    }
  };

  return (
    <>
      <OuterContainer>
        <ToolBar />
        <DroppableContainer ref={dropZoneRef} grid={flexContainer === 1 ? 1 : 2}>
          {Array.from({
            length: flexContainer,
          }).map((item, index) => {
            const title = index === 0 ? "MONEY GIVEN" : "CHANGE";
            const isShow = flexContainer === 1 || flexContainer === 2;

            let moneyGiven: any = droppedItems
              .filter((item) => item.containerIndex === 0)
              .map((item) => item.value)
              .reduce((a: number, c: number) => a + c, 0)
              .toFixed(2);

            let change: any = droppedItems
              .filter((item) => item.containerIndex === 1)
              .map((item) => item.value)
              .reduce((a: number, c: number) => a + c, 0)
              .toFixed(2);

            const droppedAmount = droppedItems
              .filter((item) => item.containerIndex === index)
              .map((item) => item.value)
              .reduce((a, c) => a + c, 0);

            return (
              <GridContainer
                key={index}
                height={100}
                isLast={isShow ? false : flexContainer % 2 !== 0 && flexContainer - 1 === index}
                isShow={isShow}
              >
                {theme === "money_lab" ? (
                  <>
                    {toggleTotal && (
                      <Chip onClick={() => checkTotal(index)} id={`chip-${index}`}>
                        {draggItems?.currencySign}
                        {targetLabel ? targetLabel : droppedAmount?.toFixed(2)}
                      </Chip>
                    )}
                    {droppedItems
                      ?.filter((item) => item.containerIndex === index)
                      ?.map((item) => {
                        return (
                          <div onMouseDown={(e) => handleDragStart(e, item, true)}>
                            <img
                              key={item.id}
                              src={item.image}
                              style={{
                                height: item.type === "coin" ? item.height : height,
                                width: item.type === "coin" ? item.width : width,
                                position: "absolute",
                                left: `${item.x}px`,
                                top: `${item.y}px`,
                                objectFit: "contain",
                              }}
                            />
                          </div>
                        );
                      })}
                  </>
                ) : (
                  <>
                    <Header>
                      {title}{" "}
                      {toggleTotal &&
                        `(${draggItems.currencySign}${title === "CHANGE" ? change : moneyGiven})`}
                    </Header>
                    {theme === "exchange" && index === 0 && (
                      <div style={{ position: "relative" }}>
                        <PriceTag
                          fill={
                            isChangeCorrect === false ? "red" : isChangeCorrect ? "green" : null
                          }
                          ref={priceTagRef}
                        />
                        <p
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-65%, -50%)",
                            color: "#ffffff",
                            fontSize: 20,
                            fontWeight: 600,
                            zIndex: 1000,
                          }}
                        >
                          ${randomCurrencyValueLabel}
                        </p>
                      </div>
                    )}

                    {droppedItems
                      ?.filter((item) => item.containerIndex === index)
                      ?.map((item) => (
                        <div onMouseDown={(e) => handleDragStart(e, item, true)}>
                          <img
                            key={item.id}
                            src={item.image}
                            style={{
                              height: item.type === "coin" ? item.height : height,
                              width: item.type === "coin" ? item.width : width,
                              position: "absolute",
                              left: `${item.x}px`,
                              top: `${item.y}px`,
                              objectFit: "contain",
                            }}
                          />
                        </div>
                      ))}
                  </>
                )}
              </GridContainer>
            );
          })}
        </DroppableContainer>
        <DraggableWrapper>
          <DraggableContainer ref={containerRef} height={pixelPosition.height}>
            {(type === "Coin & Notes" || type === "Coin") && (
              <Row justify="space-between" align="center">
                {draggItems?.data
                  .filter((item) => item.type === "coin")
                  ?.map((item: Data, index: number) => {
                    return (
                      <div onMouseDown={(e) => handleDragStart(e, item)}>
                        <img
                          color="red"
                          key={item.key}
                          src={item.image}
                          alt={`Currency ${index}`}
                          style={{
                            height: item.height ?? "13cqh",
                            width: item.width ?? "9cqw",
                            pointerEvents: "none",
                            objectFit: "contain",
                            marginBottom: 2,
                          }}
                        />
                      </div>
                    );
                  })}
              </Row>
            )}
            {(type === "Coin & Notes" || type === "Note") && (
              <Row justify="space-between">
                {draggItems?.data
                  .filter((item) => item.type === "note")
                  ?.map((item: Data, index: number) => {
                    return (
                      <div onMouseDown={(e) => handleDragStart(e, item)}>
                        <img
                          color="red"
                          key={item.key}
                          src={item.image}
                          alt={`Currency ${index}`}
                          style={{
                            height: "14cqh",
                            width: draggItems.currency === "us" ? "11cqw" : "15cqw",
                            pointerEvents: "none",
                            objectFit: "contain",
                          }}
                        />
                      </div>
                    );
                  })}
              </Row>
            )}
          </DraggableContainer>
          <OptionContainer>
            {theme === "exchange" && (
              <Button width={6} height={6} fontSize={2} onClick={checkChange}>
                Check Change
              </Button>
            )}
            <Row gap={4}>
              <GoButton Icon={Reset} onClick={reset} height="4cqw" width="4cqw" />
              {theme === "exchange" && (
                <Button height={4} width={4} onClick={() => generateRandomCurrency()}>
                  $
                </Button>
              )}
              <GoButton
                Icon={toggleTotal ? See : eyeClose}
                onClick={toggleView}
                height="4cqw"
                width="4cqw"
              />
            </Row>
          </OptionContainer>
        </DraggableWrapper>
      </OuterContainer>
      <MoveIconBtn Icon={Move} />
    </>
  );
};

export default Money;

const ToolBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  height: 6cqh;
  border-bottom: 1px solid #f0f0f0;
`;

// Styled components
const OuterContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  justify-content: space-between;
`;

const DroppableContainer = styled.div.attrs({
  className: classNames("no-drag"),
})<{ grid: number }>`
  display: grid;
  grid-template-columns: ${(props) => `repeat(${props.grid}, 1fr)`};
  grid-template-rows: auto auto;
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
`;

const GridContainer = styled.div<{
  height: number;
  isLast?: boolean;
  isShow?: boolean;
}>`
  display: flex;
  justify-content: center;
  border: 1px dashed #ccc;
  width: 100%;
  grid-column: ${(props) => (props.isLast ? "1 / span 2" : "")};
  grid-row: ${(props) => (props.isShow ? "1 / span 2" : "")};
`;

const DraggableWrapper = styled.div.attrs({
  className: classNames("no-drag"),
})`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const DraggableContainer = styled.div<{ height: number }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  position: relative;
  padding: 4px 0px 0px 8px;
`;

const OptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  height: 100%;
  padding: 8px;
  width: 15cqw;
`;

const Chip = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #8f84de;
  color: #ffffff;
  height: 10cqh;
  border-radius: 16px;
  padding: 2cqh 2cqw;
  margin-top: 6px;
  font-weight: 700;
  z-index: 99;
  font-size: 6cqh;
`;

const Button = styled.div<{
  width?: number;
  height?: number;
  fontSize?: number;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #8f84de;
  width: ${(props) => props.width ?? 5}cqw;
  height: ${(props) => props.height ?? 5}cqw;
  border-radius: 8px;
  color: #ffffff;
  font-weight: 700;
  font-size: ${(props) => props.fontSize ?? 3}cqh;
  cursor: pointer;
  text-align: center;
  line-height: 1.5;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 5cqh;
  background-color: #8f84de;
  color: #ffffff;
  font-size: 3cqh;
  font-weight: 600;
`;

const PriceTag = forwardRef((props: any, ref) => (
  <svg
    ref={ref}
    xmlns="http://www.w3.org/2000/svg"
    xmlSpace="preserve"
    className="scale-box"
    width="50cqh"
    height="50cqh"
    viewBox="0 0 59 59"
    {...props}
  >
    <path
      d="M30.22 5 1.241 34.218a2.53 2.53 0 0 0 0 3.576L21.707 58.26a2.53 2.53 0 0 0 3.576 0L53.5 29.28V5H30.22zM43.5 19a4 4 0 1 1 0-8 4 4 0 0 1 0 8z"
      style={{
        fill: props.fill ?? "#8f84de",
      }}
    />
    <path
      d="M49.5 20c-.26 0-.52-.11-.71-.29-.18-.19-.29-.45-.29-.71 0-.271.11-.53.3-.71.36-.37 1.04-.37 1.41 0 .18.189.29.439.29.71 0 .26-.11.52-.29.71-.19.18-.45.29-.71.29zM25.7 52.1a.987.987 0 0 1-.02-1.41.989.989 0 0 1 1.41-.02c.4.38.41 1.02.02 1.41-.19.2-.45.3-.72.3-.25 0-.5-.09-.69-.28zm2.91-2.991c-.39-.39-.4-1.02-.02-1.42.39-.39 1.02-.399 1.42-.02.39.39.4 1.02.02 1.42-.2.2-.46.3-.72.3a.999.999 0 0 1-.7-.28zm2.92-3a.986.986 0 0 1-.02-1.409c.38-.391 1.02-.4 1.41-.021.4.39.41 1.021.02 1.41-.2.21-.46.31-.72.31-.25 0-.5-.099-.69-.29zm2.91-2.989a.996.996 0 0 1-.02-1.41.988.988 0 0 1 1.41-.021c.4.38.41 1.021.02 1.41-.19.2-.45.3-.71.3a.99.99 0 0 1-.7-.279zm2.91-2.99c-.39-.39-.4-1.021-.02-1.42.39-.391 1.02-.4 1.42-.021.4.391.4 1.021.02 1.42a1.012 1.012 0 0 1-1.42.021zm2.92-3a.987.987 0 0 1-.02-1.41.987.987 0 0 1 1.41-.02c.4.39.41 1.02.02 1.42a.98.98 0 0 1-.71.3c-.25 0-.51-.101-.7-.29zm2.91-2.99a1 1 0 1 1 1.4-1.43c.39.38.4 1.02.02 1.41-.2.199-.46.31-.72.31-.25 0-.5-.1-.7-.29zm2.92-2.991a.999.999 0 0 1-.02-1.42c.38-.39 1.02-.399 1.41-.02.4.39.41 1.02.02 1.42-.19.2-.46.3-.72.3a.96.96 0 0 1-.69-.28zm2.4-3.799c0-.55.45-1 1-1s1 .45 1 1c0 .561-.45 1-1 1s-1-.44-1-1zm0-4.17c0-.56.45-1 1-1s1 .44 1 1c0 .55-.45 1-1 1s-1-.451-1-1zM23.48 55.38a1.01 1.01 0 0 1-1-1c0-.271.11-.53.3-.71.36-.37 1.04-.37 1.41 0 .18.18.29.439.29.71 0 .26-.11.52-.3.7a.96.96 0 0 1-.7.3zM20.15 52.46a1.008 1.008 0 0 1 0-1.42.994.994 0 0 1 1.41 0c.39.399.39 1.029 0 1.42-.19.189-.45.29-.7.29-.26 0-.52-.101-.71-.29zm-2.63-2.63a.996.996 0 0 1 0-1.41c.39-.39 1.03-.39 1.42 0 .39.39.39 1.02 0 1.41a.992.992 0 0 1-1.42 0zm-2.62-2.62a1.008 1.008 0 0 1 0-1.42.994.994 0 0 1 1.41 0c.39.399.39 1.029 0 1.42a1.005 1.005 0 0 1-1.41 0zm-2.63-2.63a.996.996 0 0 1 0-1.41c.39-.39 1.03-.39 1.42 0 .39.39.39 1.02 0 1.41a.992.992 0 0 1-1.42 0zm-2.62-2.62a1.008 1.008 0 0 1 0-1.42.994.994 0 0 1 1.41 0c.39.399.39 1.029 0 1.42-.19.189-.45.29-.7.29-.26 0-.52-.101-.71-.29zm-2.63-2.63a.996.996 0 0 1 0-1.41c.39-.39 1.03-.39 1.42 0 .39.39.39 1.02 0 1.41a.992.992 0 0 1-1.42 0zM5.11 37a.977.977 0 0 1-.71-.29.973.973 0 0 1-.29-.71c0-.26.1-.521.29-.71.37-.36 1.04-.37 1.41 0 .19.189.29.45.29.71 0 .27-.1.53-.29.71a.97.97 0 0 1-.7.29zM7.08 34.01c-.39-.39-.39-1.02-.01-1.41.39-.4 1.02-.4 1.42-.01.39.39.39 1.02 0 1.42-.19.19-.45.29-.71.29a.96.96 0 0 1-.7-.29zm2.68-2.7a.996.996 0 0 1-.01-1.41.996.996 0 1 1 1.42 1.4c-.19.2-.45.3-.71.3-.25 0-.51-.1-.7-.29zm2.67-2.701c-.39-.39-.39-1.02 0-1.42a.996.996 0 0 1 1.41 0c.4.391.4 1.021.01 1.41-.2.2-.45.3-.71.3-.26 0-.51-.099-.71-.29zm2.68-2.699a.996.996 0 0 1 0-1.41c.39-.4 1.02-.4 1.41-.01.39.39.4 1.02.01 1.409-.2.2-.46.301-.71.301-.26 0-.51-.1-.71-.29zm2.68-2.7a.996.996 0 0 1 0-1.41c.38-.4 1.02-.4 1.41-.01.39.39.39 1.02.01 1.41-.2.199-.46.3-.71.3-.26 0-.51-.101-.71-.29zm2.68-2.7a.996.996 0 0 1-.01-1.41 1 1 0 0 1 1.42-.01c.39.39.39 1.02 0 1.42a1.001 1.001 0 0 1-1.41 0zm2.68-2.7a.999.999 0 0 1-.01-1.42c.39-.39 1.02-.39 1.42 0a.996.996 0 0 1 0 1.41c-.19.2-.45.3-.71.3-.25 0-.51-.1-.7-.29zm2.68-2.701a.996.996 0 1 1 1.4-1.42c.4.391.4 1.021.01 1.41-.2.2-.45.3-.71.3a.98.98 0 0 1-.7-.29zm2.67-2.699a1.008 1.008 0 0 1 0-1.42.994.994 0 0 1 1.41 0c.39.39.4 1.02.01 1.409-.2.2-.46.301-.71.301-.26 0-.51-.1-.71-.29zM31.89 10c-.27 0-.52-.11-.71-.29-.19-.19-.29-.45-.29-.71s.1-.521.29-.71c.37-.37 1.04-.37 1.41 0 .19.189.3.45.3.71s-.11.52-.3.71c-.18.18-.44.29-.7.29zM35.19 9c0-.55.45-1 1-1s1 .45 1 1-.45 1-1 1-1-.45-1-1zM40.5 10c-.26 0-.52-.11-.71-.29-.18-.19-.29-.45-.29-.71s.11-.521.29-.71c.37-.37 1.05-.37 1.42 0 .18.189.29.45.29.71s-.11.52-.29.71c-.19.18-.45.29-.71.29z"
      style={{
        fill: "#ffc107",
      }}
    />
    <path
      d="M43.5 7a8 8 0 1 0 0 16 8 8 0 0 0 0-16zm0 12a4 4 0 1 1 0-8 4 4 0 0 1 0 8z"
      style={{
        fill: "#ffc107",
      }}
    />
    <path
      d="M43.5 16a.999.999 0 0 1-.707-1.707l14-14a.999.999 0 1 1 1.414 1.414l-14 14A.997.997 0 0 1 43.5 16z"
      style={{
        fill: "#ffc107",
      }}
    />
  </svg>
));
